import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { ListRestart, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "./ui/alert-dialog";
import { wabaDate } from "./WabaDiagnostics";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const REASONS = { not_failed: "Нет подтверждённой ошибки", delivery_confirmed: "Доставка подтверждена",
  other_error: "Другие ошибки, включая spam и недоставляемые", already_prepared: "Повтор уже подготовлен",
  missing_evidence: "Недостаточно данных об ошибке", optout: "Отписались", undeliverable: "Недоставляемые контакты", duplicate_phone: "Дубли телефонов" };
const errorText = e => typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Не удалось подготовить повтор";

export const WabaLimitRetry = ({ campaign, onPrepared }) => {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("ready");
  const [page, setPage] = useState(0);
  const requestRef = useRef(0);
  const busyRef = useRef(false);
  const load = useCallback(async () => {
    const request = ++requestRef.current;
    setLoading(true); setError("");
    try {
      const r = await axios.get(`${API}/admin/waba/campaigns/${campaign.id}/retry-limits/preview`, { withCredentials: true });
      if (request === requestRef.current) setPreview(r.data);
    } catch (e) { if (request === requestRef.current) setError(errorText(e)); }
    finally { if (request === requestRef.current) setLoading(false); }
  }, [campaign.id]);
  useEffect(() => {
    if (!open) return;
    load();
    const timer = setInterval(() => { if (!busyRef.current) load(); }, 30000);
    return () => { clearInterval(timer); requestRef.current += 1; };
  }, [open, load]);
  useEffect(() => {
    setPage(p => Math.min(p, Math.max(0, Math.ceil((preview?.[tab]?.length || 0) / 50) - 1)));
  }, [preview, tab]);
  const prepare = async () => {
    if (busyRef.current || !preview?.ready_count) return;
    busyRef.current = true;
    setBusy(true); setError("");
    try {
      const r = await axios.post(`${API}/admin/waba/campaigns/${campaign.id}/retry-limits/prepare`,
        { confirmed: true, recipient_ids: preview.ready.map(r => r.id) }, { withCredentials: true });
      setOpen(false); onPrepared(r.data.id, true);
    } catch (e) { setError(errorText(e)); }
    finally { busyRef.current = false; setBusy(false); }
  };
  const items = preview?.[tab] || [];
  return <>
    <button className="waba-tool" data-testid="waba-limit-retry-open" onClick={() => { setPreview(null); setTab("ready"); setPage(0); setOpen(true); }}>
      <ListRestart size={16} /> Подготовить повтор по лимиту
    </button>
    <AlertDialog open={open} onOpenChange={v => { if (!busy) setOpen(v); }}>
      <AlertDialogContent data-testid="waba-limit-retry-dialog" className="waba-retry-dialog waba-limit-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle data-testid="waba-limit-retry-title">Повтор по лимиту</AlertDialogTitle>
          <AlertDialogDescription data-testid="waba-limit-retry-description">
            {campaign.name} · {campaign.sender_label}. Только ошибки 131049, 130429 и 131042 (проблема с оплатой Facebook). Подготовка создаст отдельную кампанию-повтор на паузе с тем же шаблоном и текстом — без отправки сообщений.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="waba-limit-body" data-testid="waba-limit-retry-body">
        {error && <p role="alert" className="waba-danger" data-testid="waba-limit-retry-error">{error}</p>}
        <div className="waba-toolbar">
          <span className="waba-muted" data-testid="waba-limit-retry-checked">{loading ? "Проверка допуска…" : `Проверено: ${wabaDate(preview?.checked_at)}`}</span>
          <button className="waba-tool" data-testid="waba-limit-retry-refresh" disabled={loading || busy} onClick={load}><RefreshCw size={14} /> Обновить</button>
        </div>
        {preview && <>
          <div className="waba-limit-totals">
            <div data-testid="waba-limit-retry-ready-count"><span>Готовы к повтору</span><strong>{preview.ready_count}</strong></div>
            <div data-testid="waba-limit-retry-waiting-count"><span>Ещё ждут лимит</span><strong>{preview.waiting_count}</strong></div>
            <div data-testid="waba-limit-retry-excluded-count"><span>Не подходят</span><strong>{preview.excluded_count}</strong></div>
          </div>
          <p className="waba-muted" data-testid="waba-limit-retry-codes">В подборе: 131049 — {preview.ready_by_code?.["131049"] || 0}; 130429 — {preview.ready_by_code?.["130429"] || 0}; 131042 (оплата) — {preview.ready_by_code?.["131042"] || 0}.</p>
          <p className="waba-muted" data-testid="waba-limit-retry-quota">Локальный лимит: {preview.local_quota.used} / {preview.local_quota.limit} принято Meta сегодня (UTC). Остаток: {preview.local_quota.remaining}. Это не лимит Meta.</p>
          {preview.next_available_at && <p className="waba-warning" data-testid="waba-limit-retry-next-time">Следующий допуск ожидается не раньше {wabaDate(preview.next_available_at)}. Для 131049 обязательны полные 24 часа после ошибки; 130429 — пауза по темпу; 131042 — 5 минут после ошибки (сначала пополните баланс Facebook). Ограничения Meta могут сохраняться.</p>}
          {!!preview.original_queue_count && <p className="waba-warning" data-testid="waba-limit-retry-original-queue">В исходной очереди остаются {preview.original_queue_count} номеров. Они не копируются в повтор; после локального суточного лимита продолжаются в исходной кампании.</p>}
          {!!preview.excluded_count && <details data-testid="waba-limit-retry-exclusions">
            <summary data-testid="waba-limit-retry-exclusions-toggle">Почему исключены</summary>
            {Object.entries(preview.excluded).map(([key, count]) => <div key={key} className="waba-muted" data-testid={`waba-limit-exclusion-${key.replaceAll('_', '-')}`}>{REASONS[key] || key}: {count}</div>)}
          </details>}
          <div className="waba-toolbar" role="group" aria-label="Список получателей">
            <button className="waba-tool" data-testid="waba-limit-retry-ready-tab" aria-pressed={tab === "ready"} onClick={() => { setTab("ready"); setPage(0); }}>К повтору · {preview.ready_count}</button>
            <button className="waba-tool" data-testid="waba-limit-retry-waiting-tab" aria-pressed={tab === "waiting"} onClick={() => { setTab("waiting"); setPage(0); }}>Ожидают · {preview.waiting_count}</button>
          </div>
          <div className="waba-limit-phones" data-testid="waba-limit-retry-phones">
            {!items.length && <p className="waba-muted" data-testid="waba-limit-retry-empty">{tab === "ready" ? "Подходящих номеров сейчас нет. Ничего не будет отправлено." : "Нет номеров в ожидании допуска."}</p>}
            {items.slice(page * 50, page * 50 + 50).map(r => <div key={r.id} data-testid={`waba-limit-recipient-row-${r.id}`}>
              <strong data-testid={`waba-limit-phone-number-${r.id}`}>+{r.phone}</strong><span data-testid={`waba-limit-phone-code-${r.id}`}>{r.error_code}</span>
              {tab === "waiting" && <small data-testid={`waba-limit-phone-time-${r.id}`}>{r.wait_reason === "local_daily_cap" ? "Локальный суточный лимит" : "Пауза после ошибки"} · {wabaDate(r.available_at)}</small>}
            </div>)}
          </div>
          {items.length > 50 && <div className="waba-pagination">
            <button className="waba-tool" data-testid="waba-limit-retry-prev" aria-label="Предыдущие номера" disabled={!page} onClick={() => setPage(v => v - 1)}><ChevronLeft size={16} /></button>
            <span data-testid="waba-limit-retry-page">{page * 50 + 1}–{Math.min(page * 50 + 50, items.length)} из {items.length}</span>
            <button className="waba-tool" data-testid="waba-limit-retry-next" aria-label="Следующие номера" disabled={(page + 1) * 50 >= items.length} onClick={() => setPage(v => v + 1)}><ChevronRight size={16} /></button>
          </div>}
        </>}
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="waba-limit-retry-cancel" disabled={busy}>Отмена</AlertDialogCancel>
          <AlertDialogAction data-testid="waba-limit-retry-prepare" disabled={busy || loading || !preview?.ready_count || !!error}
            onClick={e => { e.preventDefault(); prepare(); }}>{busy ? "Подготовка…" : `Подготовить повтор (${preview?.ready_count || 0})`}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>;
};